/* eslint-disable no-redeclare */
import { History, parsePath } from 'history';
import { isEmpty, isNil } from 'lodash';
import { matchRoutes, RouteObject } from 'react-router';

import { basePath } from '@bai/utils';

import { AngularServiceNames, getAngularService } from '../utils';

import type { ILocationService } from 'angular';

export function waitForAngularReady({
  setIsReady,
}: {
  setIsReady: (isReady: boolean) => void;
}) {
  if (!window.angular) {
    const interval = setInterval(() => {
      if (window.angular) {
        clearInterval(interval);
        window.angular.element(document.body).ready(() => {
          setIsReady(true);
        });
      }
    }, 50);
    return () => clearInterval(interval);
  }
  const angular = window.angular.element(document.body);
  angular.ready(() => {
    setIsReady(true);
  });
}

export function isReactRoute({
  path,
  routeConfig,
}: {
  path: string;
  routeConfig: RouteObject[];
}): boolean {
  const pathWithoutBase =
    basePath.length > 1 ? path.replace(basePath, '/') : path;
  const newLocation =
    pathWithoutBase[0] !== '/'
      ? parsePath(`/${pathWithoutBase}`)
      : parsePath(pathWithoutBase);

  // @ts-ignore
  const match: [] = matchRoutes<RouteObject>(routeConfig, newLocation)?.filter(
    (m) => m.route.path !== '*',
  );

  return !isEmpty(match);
}

export function setUpLocationSync({
  isReady,
  currentPath,
  setCurrentPath,
  history,
  routeConfig,
}: {
  isReady: boolean;
  currentPath: string;
  setCurrentPath: React.Dispatch<React.SetStateAction<string>>;
  history: History;
  routeConfig: RouteObject[];
}): () => void {
  if (!isReady) return () => undefined;
  const location = getAngularService(AngularServiceNames.LOCATION);
  const rootScope = getAngularService(AngularServiceNames.ROOT_SCOPE);

  const angularUrl = location?.url || new URL(window.location.toString());

  const unlisten = history.listen((update) => {
    setCurrentPath(update.location.pathname);
    if (
      isReactRoute({ path: currentPath, routeConfig }) &&
      !isReactRoute({ path: update.location.pathname, routeConfig })
    ) {
      rootScope.$digest();
    }
  });

  /*
   * NOTE: This works for some cases, but if the legacy application uses any $location
   * method besides .url, then it won't work. We'll need to eventually replace those
   * methods as well
   */
  function newUrlHandler(): string;
  function newUrlHandler(url: string): ILocationService;
  function newUrlHandler(this: void, url?: string): ILocationService | string {
    if (isNil(url))
      return angularUrl.call(this, undefined as unknown as string);
    setCurrentPath(url);
    if (isReactRoute({ path: url, routeConfig })) {
      history.push(url);
      return location;
    }
    return angularUrl.call(this, url);
  }

  if (location) {
    location.url = newUrlHandler;
  }

  return () => {
    unlisten();
  };
}
